import React, { useEffect, useState } from 'react';
import { Loader2, Mail, Paperclip } from 'lucide-react';
import { vendorRfqsAPI } from '../../services/api';
import { pdfBlobFromIframe } from '../../pages/print-views/shared';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';

const defaultBody = (rfq) => `Dear Sir / Madam,

Please find attached our Request for Quotation ${rfq.rfq_number} along with the fillable Excel sheet and the relevant drawings.

Kindly quote your best price in ${(rfq.currency || 'INR').toUpperCase()}, along with weight per piece, MOQ, lead time, packing and delivery terms.

Thanks and regards,
Singhania International Limited`;

export const VendorRfqEmailDialog = ({ rfq, open, onOpenChange, onSent }) => {
  const [context, setContext] = useState(null);
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [drawingIds, setDrawingIds] = useState([]);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open || !rfq) { setContext(null); return; }
    vendorRfqsAPI.emailContext(rfq.id).then((response) => {
      setContext(response.data);
      setRecipients((response.data.recipients || []).join(', '));
      setSubject(response.data.subject);
      setMessage(defaultBody(rfq));
      setDrawingIds((response.data.drawings || []).filter((item) => item.matches_rfq_item).map((item) => item.id));
    }).catch(() => toast.error('Could not load vendor contacts'));
  }, [open, rfq]);

  const toggleDrawing = (id) => setDrawingIds((current) =>
    current.includes(id) ? current.filter((value) => value !== id) : [...current, id]);

  const send = async () => {
    if (!recipients.trim()) return toast.error('Add at least one vendor recipient');
    setSending(true);
    try {
      const blob = await pdfBlobFromIframe(
        '[data-testid="vendor-rfq-email-iframe"]', 'portrait', `${rfq.rfq_number}.pdf`, { pageNumbers: true });
      if (!blob || blob.size < 1024) throw new Error('The RFQ PDF came out blank');
      const formData = new FormData();
      formData.append('to_emails', recipients);
      formData.append('subject', subject);
      formData.append('message', message);
      formData.append('document_ids', drawingIds.join(','));
      formData.append('pdf_attachment', blob, `${rfq.rfq_number}.pdf`);
      const response = await vendorRfqsAPI.sendEmail(rfq.id, formData);
      toast.success('RFQ emailed to the vendor', {
        description: `${response.data.recipients.length} recipient(s) · ${response.data.attachments.length} attachment(s)`,
      });
      onOpenChange(false);
      await onSent();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Could not send the RFQ email');
    } finally { setSending(false); }
  };

  if (!rfq) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="vendor-rfq-email-dialog">
        <DialogHeader>
          <DialogTitle>Email RFQ {rfq.rfq_number}</DialogTitle>
          <DialogDescription>{rfq.vendor_name} — the branded PDF and the fillable Excel are attached automatically.</DialogDescription>
        </DialogHeader>

        {!context ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin" /></div> : (
          <div className="space-y-4">
            {!context.resend_ready && (
              <p className="border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="vendor-rfq-email-not-configured">
                Email sending is not configured on this environment.
              </p>
            )}
            <div className="space-y-2"><Label>To (all vendor contacts)</Label>
              <Input value={recipients} onChange={(event) => setRecipients(event.target.value)}
                placeholder="buyer@vendor.com, sales@vendor.com" data-testid="vendor-rfq-email-recipients-input" />
              <p className="text-xs text-muted-foreground">{(context.contacts || []).length} extra contact(s) from Vendor Master are included.</p>
            </div>
            <div className="space-y-2"><Label>Subject</Label>
              <Input value={subject} onChange={(event) => setSubject(event.target.value)} data-testid="vendor-rfq-email-subject-input" />
            </div>
            <div className="space-y-2"><Label>Message</Label>
              <Textarea rows={7} value={message} onChange={(event) => setMessage(event.target.value)} data-testid="vendor-rfq-email-message-input" />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Paperclip className="h-4 w-4" /> Attach drawings & technical documents</Label>
              {(context.drawings || []).length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="vendor-rfq-email-no-drawings">No drawings uploaded against this inquiry.</p>
              ) : (
                <div className="max-h-[170px] space-y-2 overflow-y-auto border p-3" data-testid="vendor-rfq-email-drawings">
                  {context.drawings.map((drawing) => (
                    <label key={drawing.id} className="flex items-center gap-3 text-sm">
                      <Checkbox checked={drawingIds.includes(drawing.id)} onCheckedChange={() => toggleDrawing(drawing.id)}
                        data-testid={`vendor-rfq-email-drawing-${drawing.id}`} />
                      <span className="flex-1 truncate">{drawing.filename}</span>
                      <span className="text-xs text-muted-foreground">{Math.round((drawing.file_size || 0) / 1024)} KB · {drawing.category}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <iframe title="RFQ PDF source" src={`/vendor-rfqs/${rfq.id}/print?preview=true`}
              className="h-[1px] w-[1px] opacity-0" data-testid="vendor-rfq-email-iframe" />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="vendor-rfq-email-cancel-button">Cancel</Button>
          <Button onClick={send} disabled={sending || !context?.resend_ready} data-testid="vendor-rfq-email-send-button">
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
            {sending ? 'Generating PDF & sending…' : 'Send RFQ'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
